import { useEffect, useRef, useState } from "react";
import { Send, Mic, Copy, ThumbsUp, ThumbsDown, Trash2, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "./ui/input";

const SUGGESTED = [
  "How much protein should I eat?",
  "How many calories do I need?",
  "What workout should I do today?",
  "How do I lose belly fat?",
  "What is progressive overload?",
  "Explain calorie deficit.",
  "How much water should I drink?",
  "Can I replace squats?",
  "Difference between weight loss and fat loss?",
];
const QUICK = ["Suggest another exercise", "Make it easier", "Give me a beginner version", "Recommend a recipe"];

function newSession() {
  let s = localStorage.getItem("bffy_assistant_session");
  if (!s) { s = "as_" + Math.random().toString(36).slice(2, 12); localStorage.setItem("bffy_assistant_session", s); }
  return s;
}

function Typewriter({ text, animate }) {
  const [shown, setShown] = useState(animate ? "" : text);
  useEffect(() => {
    if (!animate) { setShown(text); return; }
    let i = 0; const step = Math.max(1, Math.round(text.length / 120));
    const id = setInterval(() => { i += step; setShown(text.slice(0, i)); if (i >= text.length) clearInterval(id); }, 12);
    return () => clearInterval(id);
  }, [text, animate]);
  return <span className="whitespace-pre-wrap">{shown}</span>;
}

export default function AssistantChat({ variant = "page" }) {
  const { user } = useAuth();
  const sid = useRef(newSession());
  const [messages, setMessages] = useState([]); // {role, content, animate?}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(0);
  const [reactions, setReactions] = useState({});
  const [leadShown, setLeadShown] = useState(false);
  const [leadDone, setLeadDone] = useState(() => !!localStorage.getItem("bffy_assistant_lead"));
  const [lead, setLead] = useState({ name: "", email: "" });
  const [listening, setListening] = useState(false);
  const endRef = useRef(null);
  const recogRef = useRef(null);

  useEffect(() => {
    api.get(`/assistant/history/${sid.current}`).then((r) => {
      setMessages(r.data.map((m) => ({ role: m.role, content: m.content })));
      setCount(r.data.filter((m) => m.role === "user").length);
    }).catch(() => {});
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy, leadShown]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", { session_id: sid.current, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, animate: true }]);
      setCount(data.user_message_count);
      if (!data.authenticated && !leadDone && data.user_message_count >= 3) setLeadShown(true);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I'm having trouble right now. Please try again in a moment." }]);
    } finally { setBusy(false); }
  };

  const submitLead = async () => {
    if (!lead.name || !lead.email) { toast.error("Please enter your name and email."); return; }
    try {
      const lastQ = [...messages].reverse().find((m) => m.role === "user")?.content || "";
      await api.post("/assistant/lead", { ...lead, message: lastQ });
      localStorage.setItem("bffy_assistant_lead", "1");
      setLeadDone(true); setLeadShown(false);
      toast.success("Thanks! Coach Abhishek will reach out soon.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copy = (t) => {
    Promise.resolve(navigator.clipboard?.writeText(t))
      .then(() => toast.success("Copied"))
      .catch(() => toast.error("Couldn't copy"));
  };
  const react = (i, v) => { setReactions((r) => ({ ...r, [i]: v })); toast.success(v === "up" ? "Glad it helped!" : "Thanks — I'll improve."); };
  const clear = () => {
    localStorage.removeItem("bffy_assistant_session");
    sid.current = newSession(); setMessages([]); setCount(0); setReactions({}); setLeadShown(false);
    toast.success("Chat cleared");
  };

  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice input isn't supported in this browser."); return; }
    if (listening) { recogRef.current?.stop(); setListening(false); return; }
    const r = new SR(); recogRef.current = r; r.lang = "en-US"; r.interimResults = false;
    r.onresult = (e) => { setInput((prev) => (prev ? prev + " " : "") + e.results[0][0].transcript); };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    r.start(); setListening(true);
  };

  const recentQuestions = [...new Set(messages.filter((m) => m.role === "user").map((m) => m.content))].slice(-4).reverse();

  return (
    <div className="flex flex-col h-full" data-testid="assistant-chat">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="grid place-items-center w-10 h-10 rounded-full bg-primary/15 text-primary"><Sparkles size={20} /></div>
          <div><div className="font-display font-800 uppercase text-2xl leading-none tracking-tight">AI Fitness Assistant</div><div className="text-[11px] text-muted-foreground flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Online 24/7 · evidence-based</div></div>
        </div>
        {messages.length > 0 && <button onClick={clear} data-testid="assistant-clear" className="flex items-center gap-1.5 text-xs uppercase font-bold text-muted-foreground hover:text-primary transition-colors"><Trash2 size={14} /> Clear</button>}
      </div>

      <div className="flex-1 overflow-y-auto rounded-xl border border-border bg-background p-4 space-y-4" data-testid="assistant-messages">
        {messages.length === 0 && !busy && (
          <div className="h-full flex flex-col items-center justify-center text-center px-2">
            <Sparkles size={34} className="text-primary" />
            <p className="font-display font-700 uppercase text-2xl mt-3">Ask me anything about fitness</p>
            <p className="text-muted-foreground text-sm mt-1 max-w-sm">Nutrition, workouts, form, motivation — instant, science-based answers.</p>
            <div className="flex flex-wrap justify-center gap-2 mt-5 max-w-md">
              {SUGGESTED.slice(0, 6).map((s) => (
                <button key={s} data-testid="assistant-suggested" onClick={() => send(s)} className="text-xs border border-border rounded-full px-3.5 py-2 hover:border-primary hover:text-primary transition-colors">{s}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] ${m.role === "user" ? "" : "w-full"}`}>
              <div data-testid={`assistant-msg-${m.role}`} className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-secondary text-foreground rounded-bl-sm"}`}>
                {m.role === "assistant" ? <Typewriter text={m.content} animate={m.animate} /> : m.content}
              </div>
              {m.role === "assistant" && (
                <div className="flex items-center gap-3 mt-1.5 px-1 text-muted-foreground">
                  <button onClick={() => copy(m.content)} data-testid="assistant-copy" className="hover:text-primary transition-colors" title="Copy"><Copy size={14} /></button>
                  <button onClick={() => react(i, "up")} className={`transition-colors ${reactions[i] === "up" ? "text-primary" : "hover:text-primary"}`} title="Helpful"><ThumbsUp size={14} /></button>
                  <button onClick={() => react(i, "down")} className={`transition-colors ${reactions[i] === "down" ? "text-primary" : "hover:text-primary"}`} title="Not helpful"><ThumbsDown size={14} /></button>
                </div>
              )}
            </div>
          </div>
        ))}

        {busy && <div className="flex justify-start"><div className="bg-secondary px-4 py-3 rounded-2xl rounded-bl-sm text-sm text-muted-foreground flex gap-1"><span className="animate-bounce">●</span><span className="animate-bounce" style={{ animationDelay: "0.15s" }}>●</span><span className="animate-bounce" style={{ animationDelay: "0.3s" }}>●</span></div></div>}

        {leadShown && !leadDone && (
          <div className="border border-primary/40 bg-primary/5 rounded-xl p-4" data-testid="assistant-lead-form">
            <p className="font-bold text-sm">Want tailored guidance from Coach Abhishek?</p>
            <p className="text-xs text-muted-foreground mt-1">Drop your details and get a free follow-up. Keep chatting meanwhile!</p>
            <div className="grid sm:grid-cols-2 gap-2 mt-3">
              <Input data-testid="assistant-lead-name" placeholder="Name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="h-10" />
              <Input data-testid="assistant-lead-email" placeholder="Email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="h-10" />
            </div>
            <div className="flex gap-2 mt-2">
              <button onClick={submitLead} data-testid="assistant-lead-submit" className="bg-primary text-primary-foreground px-4 py-2 rounded-full text-xs uppercase font-bold flex items-center gap-1"><Check size={13} /> Send</button>
              <button onClick={() => setLeadShown(false)} className="text-xs uppercase font-bold text-muted-foreground px-3">Maybe later</button>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Quick replies / recent */}
      {messages.length > 0 && (
        <div className="flex gap-2 overflow-x-auto py-2 no-scrollbar" data-testid="assistant-quick">
          {(recentQuestions.length ? [...QUICK, ...recentQuestions] : QUICK).slice(0, 6).map((q, i) => (
            <button key={i} onClick={() => send(q)} className="shrink-0 text-xs border border-border rounded-full px-3 py-1.5 hover:border-primary hover:text-primary transition-colors">{q}</button>
          ))}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-1 flex gap-2 items-center">
        <button type="button" onClick={toggleVoice} data-testid="assistant-voice" className={`grid place-items-center w-11 h-11 rounded-full border transition-colors shrink-0 ${listening ? "border-primary text-primary animate-pulse" : "border-border hover:border-primary"}`} title="Voice input"><Mic size={18} /></button>
        <input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask a fitness question…" className="flex-1 h-11 px-4 rounded-full border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm" />
        <button type="submit" data-testid="assistant-send" disabled={busy || !input.trim()} className="grid place-items-center w-11 h-11 rounded-full bg-primary text-primary-foreground disabled:opacity-50 shrink-0"><Send size={18} /></button>
      </form>
      <p className="text-[10px] text-muted-foreground text-center mt-1.5">Educational only — not medical advice. Consult a doctor for medical concerns.</p>
    </div>
  );
}
